import type {
  ProfileReviewRole,
  ProfileReviewStats,
  ProfileReviewsPage,
  Review,
  ReviewEligibility,
  SubmitReviewInput,
} from "@/lib/reviews/schema";

async function parseReviewError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => null)) as
    | { code?: string; message?: string; error?: string }
    | null;
  if (typeof body?.code === "string") return body.code.toLowerCase();
  if (typeof body?.error === "string") return body.error;
  if (typeof body?.message === "string") return body.message;
  return "submit_failed";
}

export async function fetchReviewEligibility(
  sellerOrderId: string,
  signal?: AbortSignal,
): Promise<ReviewEligibility> {
  const res = await fetch(
    `/api/reviews/eligibility/${encodeURIComponent(sellerOrderId)}`,
    { credentials: "include", signal },
  );
  if (!res.ok) throw new Error(await parseReviewError(res));
  return (await res.json()) as ReviewEligibility;
}

export async function submitReviewRequest(
  input: SubmitReviewInput,
  signal?: AbortSignal,
): Promise<Review> {
  const res = await fetch("/api/reviews", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
    signal,
  });
  if (!res.ok) throw new Error(await parseReviewError(res));
  return (await res.json()) as Review;
}

export async function fetchProfileReviewsPage(opts: {
  profileId: string;
  role: ProfileReviewRole;
  offset?: number;
  limit?: number;
  signal?: AbortSignal;
}): Promise<ProfileReviewsPage> {
  const limit = opts.limit ?? 20;
  const offset = opts.offset ?? 0;
  const params = new URLSearchParams({
    role: opts.role,
    limit: String(limit),
    offset: String(offset),
  });
  const res = await fetch(
    `/api/reviews/profile/${encodeURIComponent(opts.profileId)}?${params.toString()}`,
    { credentials: "include", signal: opts.signal },
  );
  if (!res.ok) throw new Error(await parseReviewError(res));
  const raw = (await res.json()) as {
    reviews?: Review[];
    stats?: ProfileReviewStats;
    has_more?: boolean;
    total_count?: number;
  };
  return {
    reviews: Array.isArray(raw.reviews) ? raw.reviews : [],
    stats: raw.stats,
    has_more: Boolean(raw.has_more),
    total_count: typeof raw.total_count === "number" ? raw.total_count : undefined,
  };
}
